/**
 * CTA 다국어 변환 공통 유틸 (Case A — Static Translation).
 *
 * docs/cross-cutting/widget/CTA_MULTILINGUAL.md · CTA_TOOL_DIRECTIVE.md 참조.
 *
 * 위젯 카드의 클릭 핸들러에서 호출하면:
 *  1. MCP POST /api/widget/translate_cta 로 카탈로그 lookup
 *  2. 사전 번역된 발화문을 받아 chat.send_prompt 로 호스트 전송
 *  3. API 실패 시 fallback (영어 원문) 으로 흐름 유지
 *
 * MCP 도구는 CTA 메시지를 알 필요가 없다 (관심사 분리).
 */

import { postToHost, type ToolCallDirective } from "./postmessage";

// agent /api/widget/* 프록시는 인증키 불요 (agent 가 서버측 MCP_API_KEY 로 mcp 호출).
const API_BASE = import.meta.env.VITE_API_BASE || "";

/** Widget iframe URL 쿼리에서 locale 추출. build_widget_meta 가 채워준다. */
export function getLocale(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("locale") || "uk";
  } catch {
    return "uk";
  }
}

/**
 * Widget iframe URL 쿼리에서 대화 언어(lang) 추출.
 * Agent 가 ``detect_response_language()`` 로 감지한 언어 이름 (e.g. ``"Korean"``).
 * CTA 번역 시 사이트 locale 보다 우선 사용된다.
 */
export function getLang(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("lang") || "";
  } catch {
    return "";
  }
}

export interface CtaTranslateOptions {
  /** i18n 코드 (예: "recommend.add_1x_to_my_cart"). 인벤토리 SoT 의 식별자. */
  code: string;
  params?: Record<string, string | number>;
  sessionToken: string;
  fallback: string;
  locale?: string;
  /** 결정적 도구/시나리오 호출 지시문. 존재 시 LLM plan_act 를 우회한다. */
  toolCall?: ToolCallDirective;
}

async function translate(opts: CtaTranslateOptions): Promise<string> {
  const locale = opts.locale ?? getLocale();
  const lang = getLang();
  try {
    const res = await fetch(`${API_BASE}/api/widget/translate_cta`, {
      method: "POST",
      headers: {
        "Mcp-Session-Id": opts.sessionToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code: opts.code,
        locale,
        lang,
        params: opts.params || {},
      }),
      credentials: "include",
    });
    if (!res.ok) {
      console.warn(`[ctaTranslate] failed status=${res.status}, using fallback`);
      return opts.fallback;
    }
    const json = (await res.json()) as { message: string };
    return json.message || opts.fallback;
  } catch (e) {
    console.warn("[ctaTranslate] error:", e);
    return opts.fallback;
  }
}

/**
 * CTA 키를 변환하여 chat.send_prompt 로 호스트에 전송.
 *
 * @example
 * ctaTranslate({
 *   code: "recommend.add_1x_to_my_cart",
 *   params: { name: data.name, sku: data.sku },
 *   sessionToken,
 *   fallback: `Add 1x ${data.name} (${data.sku}) to my cart`,
 * });
 */
export async function ctaTranslate(opts: CtaTranslateOptions): Promise<void> {
  const message = await translate(opts);
  postToHost(
    "chat.send_prompt",
    { message },
    { sessionId: opts.sessionToken, toolCall: opts.toolCall },
  );
}

/**
 * 변환된 발화문 문자열만 반환 (postToHost 호출 없음).
 * 폼 위젯처럼 클라이언트 입력값과 결합한 후 직접 전송하는 경우 사용.
 *
 * @example
 * const tpl = await ctaTranslateText({
 *   code: "email_verify.track_order_with_email",
 *   sessionToken,
 *   fallback: "Track order {orderNumber} with email {email}",
 * });
 * const message = tpl.replace("{orderNumber}", orderNumber).replace("{email}", email);
 * postToHost("chat.send_prompt", { message }, { sessionId: sessionToken });
 */
export async function ctaTranslateText(
  opts: CtaTranslateOptions,
): Promise<string> {
  return translate(opts);
}
